import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  IDuration,
  QueryType,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { isNil } from 'lodash';
import styled from 'styled-components';
import { SingleSelectMenu } from '../../Dropdown';
import { MaybeNil } from '../../../utils/interfaces';
import { conversionWindowTimeUnitToText } from '../utils/partitionUtils';
import {
  DAY_PARTITION_ERROR_MESSAGE,
  isMoreThanMonth,
  isMoreThanOneDay,
  TIME_TO_CONVERT_PARTITION_ERROR_MESSAGE,
} from './utils';
import { colors } from '../../../config/styles';
import { ContextualMenuContext } from '../../Dropdown/ContextualMenu/KubitContextualMenu';
import { PrimaryButton } from '../../Button';
import { NumberInputField } from '../../Form';
import { onEnterAdapter } from '../../../utils/inputUtils';
import { z } from 'zod';
import {
  isZodFieldErrors,
  zodFieldErrors,
  zodFormErrors,
} from '../../../utils/zod';
import { assertCondition, Nullable } from '../../../utils/types';
import { useAppSelector } from '../../../redux/hooks';
import { builderDefaultOptionsSelector } from '../../../redux/selectors/builder';
import { MAX_INTEGER } from '../../../config/constants';

interface IProps {
  isDayPartitionSelected: boolean;
  isTimeToConvertMode: boolean;
  conversionWindow: MaybeNil<IDuration>;
  setConversionWindow: (duration: IDuration) => unknown;
  onApply: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 370px;
  padding: 12px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const ApplyAction = styled(PrimaryButton)`
  width: 100%;
`;

const ErrorMessage = styled.div`
  max-width: 179px;
  color: ${colors.mainError};
`;

const canApply = (duration: Nullable<IDuration>): duration is IDuration => {
  return !isNil(duration.value) && !isNil(duration.timeUnit);
};

export default ({
  isDayPartitionSelected,
  isTimeToConvertMode,
  conversionWindow,
  setConversionWindow,
  onApply,
}: IProps) => {
  const contextualMenuContext = useContext(ContextualMenuContext);

  const defaultOptions = useAppSelector((state) =>
    builderDefaultOptionsSelector(state),
  );

  assertCondition(
    defaultOptions?.type === QueryType.Funnel ||
      defaultOptions?.type === QueryType.Path,
  );

  const durationTimeUnits = defaultOptions.partitionWindowTimeUnits;
  const defaultTimeUnit = defaultOptions.defaultPartitionWindowTimeUnit;

  const { timeUnit, value } = isNil(conversionWindow)
    ? ({} satisfies Partial<IDuration>)
    : conversionWindow;

  const [selectedTimeUnit, setSelectedTimeUnit] =
    useState<TimeUnit>(defaultTimeUnit);
  const [durationValue, setDurationValue] = useState<number | null>(null);

  const formSchema = useMemo(() => {
    return z
      .object({
        __typename: z.literal('Duration'),
        value: z.number().positive().lte(MAX_INTEGER).nullable(),
        timeUnit: z.nativeEnum(TimeUnit),
      })
      .superRefine((arg, ctx) => {
        const { value, timeUnit } = arg;
        if (!isNil(value)) {
          if (isDayPartitionSelected && isMoreThanOneDay(timeUnit, value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: DAY_PARTITION_ERROR_MESSAGE,
              fatal: true,
            });
            return z.NEVER;
          }

          if (isTimeToConvertMode && isMoreThanMonth(timeUnit, value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: TIME_TO_CONVERT_PARTITION_ERROR_MESSAGE,
              fatal: true,
            });
            return z.NEVER;
          }
        }
      });
  }, [isDayPartitionSelected, isTimeToConvertMode]);

  const parseResult = formSchema.safeParse({
    __typename: 'Duration',
    value: durationValue,
    timeUnit: selectedTimeUnit,
  } satisfies Nullable<IDuration>);
  const errors = !parseResult.success ? parseResult.error : null;

  const handleApply = () => {
    if (parseResult.success && canApply(parseResult.data)) {
      setConversionWindow(parseResult.data);
      onApply();
    }
  };

  const options = durationTimeUnits.map((t) => ({
    value: t,
    text: conversionWindowTimeUnitToText(t, durationValue),
  }));

  const handleChangeTimeUnit = (selectedUnit: string) => {
    setSelectedTimeUnit(selectedUnit as TimeUnit);
  };

  useEffect(() => {
    if (!isNil(timeUnit)) {
      setSelectedTimeUnit(timeUnit);
    }
    if (!isNil(value)) {
      setDurationValue(value);
    }
  }, [timeUnit, value]);

  useEffect(() => {
    contextualMenuContext?.disableClose(true);
    return () => contextualMenuContext?.disableClose(false);
  }, [contextualMenuContext]);

  const formErrorMessage = zodFormErrors(errors);

  return (
    <Container>
      <Row>
        <NumberInputField
          value={durationValue}
          placeholder="e.g. 5"
          onChange={setDurationValue}
          hasError={isZodFieldErrors(errors, 'value')}
          onKeyDown={onEnterAdapter(() => handleApply())}
          errorMessage={zodFieldErrors(errors, 'value')}
        />
        <SingleSelectMenu
          options={options}
          value={selectedTimeUnit}
          onChange={handleChangeTimeUnit}
          customWidth="96px"
        />
      </Row>
      {!isNil(formErrorMessage) && (
        <ErrorMessage>{formErrorMessage}</ErrorMessage>
      )}
      <Row>
        <ApplyAction
          type="button"
          onClick={handleApply}
          disabled={!parseResult.success || !canApply(parseResult.data)}
          trackContext="conversion window"
          trackAction="apply"
        >
          Apply
        </ApplyAction>
      </Row>
    </Container>
  );
};
